import { action, makeAutoObservable, reaction } from 'mobx';
import { logger } from '@cutwise/utils';

import { sortCollectionsByDate } from '@/collection/helpers/sortCollectionsByDate';
import { Rollout } from '@/common/constants/rollouts';
import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import AppStore from '@/common/stores/AppStore';
import { appStore } from '@/common/stores/AppStore';
import { DriveAPI } from '@/drive/api/DriveAPI';
import { TYPE_DIAMOND_COLORLESS } from '@/product/constants/productTypes';
import Product, { ProductId, ProductModel } from '@/product/entity/Product';
import ProductFactory from '@/product/entity/ProductFactory';
import token from '@/user/services/token';
import UserStore from '@/user/stores/UserStore';
import { userStore } from '@/user/stores/UserStore';

import CollectionsAPI from '../api/CollectionsAPI';
import Collection, {
  COLLECTION_TYPE_DIAMOND,
  COLLECTION_TYPE_JEWELRY,
  COLLECTION_TYPE_ROUGH,
} from '../entity/Collection';
import { CollectionType } from '../entity/CollectionType';

class CollectionsStore {
  collectionsByType: Record<number, Nullable<Collection[]>> = {
    [COLLECTION_TYPE_DIAMOND]: null,
    [COLLECTION_TYPE_JEWELRY]: null,
    [COLLECTION_TYPE_ROUGH]: null,
  };

  activeCollection: Nullable<Collection> = null;

  collectionsPromises: Partial<Record<CollectionType, Promise<void>>> = {};

  private readonly collectionsAPI: CollectionsAPI;

  private readonly driveAPI: DriveAPI;

  constructor(
    public appStore: AppStore,
    public userStore: UserStore,
  ) {
    makeAutoObservable<CollectionsStore, 'collectionsAPI' | 'driveAPI'>(this, {
      collectionsPromises: false,
      collectionsAPI: false,
      driveAPI: false,
      appStore: false,
      userStore: false,
    });

    this.collectionsAPI = new CollectionsAPI();
    this.driveAPI = new DriveAPI();

    this.initCollections();

    reaction(() => this.activeProductTypeContext, this.initCollections);

    reaction(
      () => userStore?.user,
      (user) => {
        if (!user?.isAnonymous) {
          this.initCollections();
        }
      },
    );
  }

  get activeProductTypeContext() {
    return this.appStore.productTypeContext || TYPE_DIAMOND_COLORLESS;
  }

  get collections(): Nullable<Collection[]> {
    const cType = Collection.getTypeByProductType(this.activeProductTypeContext);

    if (!this.collectionsByType[cType]) {
      return null;
    }

    const filteredCollectionsByType = this.collectionsByType[cType]!.filter((c) => c.type === cType);

    return sortCollectionsByDate(filteredCollectionsByType);
  }

  initCollections = () => {
    if (token.isAuthorized && !this.userStore.user?.hasRollout(Rollout.Drive)) {
      this.fetchCollections();
    }

    if (!this.collections || !this.collections.find((c) => c === this.activeCollection)) {
      this.activeCollection = null;
    }
  };

  fetchCollections() {
    const collectionType = Collection.getTypeByProductType(this.activeProductTypeContext);

    if (this.collectionsPromises[collectionType]) {
      return;
    }

    this.collectionsPromises[collectionType] = this.collectionsAPI.fetchOwned(collectionType).then(
      action((res) => {
        const errorMessage = !res.ok ? (res.content as unknown as Nullable<{ message?: string }>)?.message : null;

        if (errorMessage && res.status !== 404) {
          Notifier.error(errorMessage);
        }

        const collections: Nullable<Collection[]> = res.content
          ? res.content.map((d) => new Collection(d, this.activeProductTypeContext))
          : null;

        this.collectionsByType[collectionType] =
          collections && collections.length > 0
            ? [...collections.filter((c) => c.updatedAt !== null), ...collections.filter((c) => c.updatedAt === null)]
            : [];
      }),
    ).catch((e) => logger.error(e));
  }

  createNewCollection = (title: string, product: Nullable<Product> = null) => {
    return this.collectionsAPI.create(this.activeProductTypeContext, title).then(
      action((res) => {
        const collection = new Collection(res.data, this.activeProductTypeContext);

        if (product) {
          this.addProductToCollection(product, collection);
        }

        const collectionType = Collection.getTypeByProductType(this.activeProductTypeContext);

        this.collectionsByType[collectionType]!.unshift(collection);

        this.activeCollection = collection;
      }),
    );
  };

  addProductToCollection = (product: Product, collection: Collection) => {
    collection.products = [...(collection.products ?? []), product];

    return this.driveAPI
      .addToCollection(collection.drive.collectionNodeId, [product.id], false)
      .then(() => {
        if (product.b2bSid) {
          Notifier.success(`${product.b2bSid} was added to collection "${collection.title}"`);
        }
      })
      .catch(
        action((error: unknown) => {
          collection.products = collection.products.filter((p) => p.id !== product.id);

          throw error;
        }),
      );
  };

  removeProductFromCollection = (product: Product, collection: Collection) => {
    const removedIndex = collection.products.findIndex((p) => p.id === product.id);

    collection.products = collection.products.filter((p) => p.id !== product.id);

    return this.driveAPI
      .removeFromCollection(collection.drive.collectionNodeId, [product.id])
      .then(() => {
        if (product.b2bSid) {
          Notifier.success(`${product.b2bSid} was removed from collection "${collection.title}"`);
        }
      })
      .catch(
        action((error: unknown) => {
          if (removedIndex >= 0) {
            const restoredProducts = [...collection.products];

            restoredProducts.splice(removedIndex, 0, product);
            collection.products = restoredProducts;
          }

          throw error;
        }),
      );
  };

  toggleActive = (c: Collection) => {
    if (this.activeCollection === c) {
      this.activeCollection = null;
    } else {
      this.setActiveCollection(c);
    }
  };

  remove = (c: Collection) => {
    const { title } = c;

    if (this.activeCollection === c) {
      this.activeCollection = null;
    }

    const cType = Collection.getTypeByProductType(this.activeProductTypeContext);

    if (!this.collectionsByType[cType]) {
      return;
    }

    this.collectionsByType[cType] = this.collectionsByType[cType]!.filter((cItem) => cItem.id !== c.id);

    this.driveAPI
      .deleteById(c.drive.collectionNodeId)
      .then(
        action((isRemoved) => {
          if (isRemoved) {
            Notifier.success(`Collection ${title} was removed`);
          } else {
            this.collectionsByType[cType]!.push(c);
            Notifier.error(`Collection ${title} was not removed`);
          }
        }),
      )
      .catch(
        action(() => {
          this.collectionsByType[cType]!.push(c);
        }),
      );
  };

  setActiveCollection = (c: Collection) => {
    this.activeCollection = c;
    this.collectionsAPI.touchCollection(c);
  };

  addProductsToCollection = (collection: Collection, productIds: ProductId[]): Promise<void> => {
    return this.driveAPI.addToCollection(collection.drive.collectionNodeId, productIds, false).then(
      action(() => {
        productIds.forEach((pid) => {
          const product = collection.products.find((p) => p.id === pid);

          if (!product) {
            collection.products.push(
              ProductFactory.make(this.appStore.productTypeContext, { id: pid } as ProductModel),
            );
          }
        });
      }),
    );
  };

  addCollection(collection: Collection) {
    const collectionType = Collection.getTypeByProductType(this.appStore.productTypeContext);

    if (!this.collectionsByType[collectionType]) {
      this.collectionsByType[collectionType] = [];
    }

    this.collectionsByType[collectionType]!.push(collection);
  }
}

export const collectionsStore = new CollectionsStore(appStore, userStore);
export default CollectionsStore;

export const useCollectionsStore = () => useStore(CollectionsStore);
